<script setup>
import { reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useContactNotes } from 'dashboard/composables/useContactNotes';

import Editor from 'dashboard/components-next/Editor/Editor.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ContactNoteItem from 'dashboard/components-next/Contacts/ContactsSidebar/components/ContactNoteItem.vue';

// "Notas" tab: composer card ("Solo visible para el equipo" + Guardar nota)
// followed by one card per note. Same store and shortcuts as the old
// sidebar panel. La lista incluye también las notas privadas escritas
// dentro de las conversaciones del contacto, que se leen aquí pero se
// siguen gestionando en su hilo.
const props = defineProps({
  contact: { type: Object, required: true },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();

const state = reactive({ message: '' });

const currentUser = useMapGetter('getCurrentUser');
const contactId = computed(() => props.contact.id);
const {
  allNotes: notes,
  isFetchingContactNotes: isFetchingNotes,
  isCreatingContactNote: isCreatingNote,
} = useContactNotes(contactId);

const goToConversation = displayId => {
  if (!displayId) return;
  router.push({
    name: 'inbox_conversation',
    params: { accountId: route.params.accountId, conversation_id: displayId },
  });
};

const getWrittenBy = note => {
  const isCurrentUser = note?.user?.id === currentUser.value.id;
  return isCurrentUser
    ? t('CONTACTS_LAYOUT.SIDEBAR.NOTES.YOU')
    : note?.user?.name || 'Bot';
};

const onAdd = content => {
  if (!content) return;
  store.dispatch('contactNotes/create', {
    content,
    contactId: props.contact.id,
  });
  state.message = '';
};

const onDelete = noteId => {
  if (!noteId) return;
  store.dispatch('contactNotes/delete', {
    noteId,
    contactId: props.contact.id,
  });
};

useKeyboardEvents({
  '$mod+Enter': {
    action: () => onAdd(state.message),
    allowOnFocusedInput: true,
  },
});
</script>

<template>
  <div class="flex flex-col gap-3">
    <section
      class="flex flex-col gap-2.5 px-4 py-3.5 rounded-xl border border-n-weak bg-n-solid-2"
    >
      <Editor
        v-model="state.message"
        :placeholder="t('CONTACTS_LAYOUT.SIDEBAR.NOTES.PLACEHOLDER')"
        :show-character-count="false"
        class="[&>div]:!border-transparent [&>div]:!bg-transparent [&>div]:px-0 [&>div]:py-0"
      />
      <div class="flex items-center gap-2">
        <span class="text-xs text-n-slate-9">
          {{ t('CONTACTS_LAYOUT.DETAILS.NOTES_TAB.PRIVATE_HINT') }}
        </span>
        <Button
          :label="t('CONTACTS_LAYOUT.SIDEBAR.NOTES.SAVE')"
          size="sm"
          class="ml-auto"
          :is-loading="isCreatingNote"
          :disabled="!state.message || isCreatingNote"
          @click="onAdd(state.message)"
        />
      </div>
    </section>

    <div
      v-if="isFetchingNotes"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <template v-else-if="notes.length">
      <div
        v-for="note in notes"
        :key="note.key"
        class="px-4 py-3.5 rounded-xl border border-n-weak bg-n-solid-2"
      >
        <ContactNoteItem
          class="!border-b-0 !pb-0"
          :note="note"
          :written-by="getWrittenBy(note)"
          :allow-delete="note.isContactNote"
          @delete="onDelete"
        />
        <button
          v-if="!note.isContactNote"
          type="button"
          class="inline-flex items-center gap-1 mt-2 text-xs font-medium text-n-slate-10 hover:text-n-blue-text transition-colors"
          @click="goToConversation(note.conversationDisplayId)"
        >
          <span class="i-lucide-message-square size-3" />
          {{
            t('CONTACTS_LAYOUT.SIDEBAR.NOTES.FROM_CONVERSATION', {
              id: note.conversationDisplayId,
            })
          }}
        </button>
      </div>
    </template>
    <p
      v-else
      class="px-6 py-8 m-0 text-sm leading-6 text-center rounded-xl border border-n-weak bg-n-solid-2 text-n-slate-11"
    >
      {{ t('CONTACTS_LAYOUT.SIDEBAR.NOTES.EMPTY_STATE') }}
    </p>
  </div>
</template>
